import { type FC, StrictMode } from 'react'
import { Icon, SingleSelect, TextInput } from '@opallabs/lapidary'
import * as styles from './styles'
import { type SimplifiedOption } from './types'
import { usePairingFormState, validateOptionName } from './useFormState'

const ids = {
  optionsColumnHeader: `${styles.rootClass}-column-title-options`,
  titleColumnHeader: `${styles.rootClass}-column-title-title`,
} as const

export type RowProps = Readonly<{
  option: SimplifiedOption
}>
export const Row: FC<RowProps> = ({ option }) => {
  const {
    duplicateOptionNames,
    entriesWithDuplicateLabelIds,
    entriesWithDuplicateOptionNames,
    isReadonly,
    labels,
    updateOption,
  } = usePairingFormState()

  const selectOptions = labels.map(({ id, name }) => ({
    label: name,
    value: id,
  }))

  const hasLabelConflict = option.labelId
    ? entriesWithDuplicateLabelIds.has(option.pseudoId)
    : false

  const selectedOption = option.labelId
    ? (selectOptions.find(({ value }) => value === option.labelId) ?? null)
    : null

  const validator = (value: string) => {
    const normalized = value.trim()
    const isDuplicate = duplicateOptionNames.has(normalized)

    return validateOptionName(value, {
      isDuplicate,
      labelId: option.labelId,
    })
  }

  // Force immediate validation if this entry has the same option name as another entry, even if the user hasn't edited
  // this entry's option name yet. Without this the validation message will only appear on fields the user *has* edited.
  const previouslyModified =
    isReadonly || entriesWithDuplicateOptionNames.has(option.pseudoId)

  return (
    <StrictMode>
      <fieldset className={styles.classes.row} css={styles.row}>
        {/* The `<TextInput>` component has the input and error message as top-level siblings; to avoid layout issues
        `<TextInput>` must be wrapped in a containing element. */}
        <div className={styles.classes.textInput}>
          <TextInput
            additionalAriaLabels={{
              'aria-labelledby': ids.optionsColumnHeader,
            }}
            disabled={isReadonly}
            value={option.optionName}
            onChange={(name) => updateOption({ ...option, optionName: name })}
            previouslyModified={previouslyModified}
            required={!!option.labelId}
            validator={validator}
          />
        </div>

        <Icon
          ariaHidden={true}
          glyph="gem-link"
          height={styles.iconSize}
          width={styles.iconSize}
        />

        <SingleSelect.ControlledRoot
          aria-labelledby={ids.titleColumnHeader}
          selectedOption={selectedOption}
          onChange={(label) => {
            updateOption({
              ...option,
              labelId: label?.value,
            })
          }}
          options={selectOptions}
        >
          <SingleSelect.SearchableBody isClearable={true}>
            <SingleSelect.Trigger
              disabled={isReadonly}
              hasError={hasLabelConflict}
              hideTrailingNode={true}
            >
              <SingleSelect.TriggerText />
            </SingleSelect.Trigger>
            <SingleSelect.Menu>
              <SingleSelect.MenuItems hideTrailingNodeUnlessSelected={true} />
            </SingleSelect.Menu>
          </SingleSelect.SearchableBody>
        </SingleSelect.ControlledRoot>
      </fieldset>
    </StrictMode>
  )
}
Row.displayName = 'AdminConfiguration: Table.Row ← PairingForm ← CustomFields'

export const Root = ({ children }: { children?: React.ReactNode }) => (
  <StrictMode>
    <div className={styles.classes.tableRoot} css={styles.tableRoot}>
      <div className={styles.classes.header}>
        <span
          className={styles.classes.columnTitle}
          id={ids.optionsColumnHeader}
        >
          Dropdown Item
        </span>
        <span className={styles.classes.columnTitle} id={ids.titleColumnHeader}>
          Label
        </span>
      </div>

      {children}
    </div>
  </StrictMode>
)
Root.displayName = 'AdminConfiguration: Table.Root ← PairingForm ← CustomFields'
